import { memo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Skeleton,
  Alert,
} from "@mui/material";
import { FileText, Download, ExternalLink, AlertCircle } from "lucide-react";

const FileViewDialog = memo(({ open, onClose, file, fileName }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [open, file]);

  if (!file || typeof file !== "string") return null;

  const displayFileName = fileName || "document";

  const getExtensionSource = (file, fileName) =>
    `${fileName || ""} ${file || ""}`.toLowerCase();

  const isImage =
    /\.(jpeg|jpg|png|webp|gif)(\?|$)/i.test(
      getExtensionSource(file, displayFileName),
    ) || file?.type?.startsWith?.("image/");
  const isPDF =
    /\.pdf(\?|$)/i.test(getExtensionSource(file, displayFileName)) ||
    file?.type === "application/pdf";

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      const response = await fetch(file, { cache: "no-store" });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const extension = new URL(file, window.location.href).pathname.match(/\.[a-z0-9]+$/i)?.[0];
      const name = displayFileName.split(/[?#]/)[0];
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = extension && !name.toLowerCase().endsWith(extension.toLowerCase())
        ? `${name}${extension}` : name;
      try {
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    } catch {
      setDownloadError("Unable to download the document. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenURL = () => {
    window.open(file, "_blank");
  };

  const handleLoad = () => setIsLoading(false);
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, color: "#1f2937" }}>
        {displayFileName}
      </DialogTitle>

      <DialogContent>
        {downloadError && <Alert severity="error">{downloadError}</Alert>}
        <Box sx={{ mt: 2 }}>
          {hasError ? (
            <Box
              sx={{
                p: 4,
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <AlertCircle size={64} style={{ color: "#dc2626" }} />
              <Typography variant="body2" sx={{ color: "#991b1b", mt: 2 }}>
                Couldn't load preview for {displayFileName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#6b7280", mt: 0.5, fontSize: "12px" }}
              >
                Try opening or downloading the file instead.
              </Typography>
            </Box>
          ) : isImage ? (
            <Box sx={{ position: "relative" }}>
              {isLoading && (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    width: "100%",
                    height: "500px",
                    borderRadius: "8px",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                />
              )}
              <Box
                component="img"
                src={file}
                onLoad={handleLoad}
                onError={handleError}
                sx={{
                  width: "100%",
                  maxHeight: "500px",
                  objectFit: "contain",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  visibility: isLoading ? "hidden" : "visible",
                  display: isLoading ? "block" : "block",
                  height: isLoading ? "500px" : "auto",
                }}
                alt={displayFileName}
              />
            </Box>
          ) : isPDF ? (
            <Box sx={{ position: "relative" }}>
              {isLoading && (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    width: "100%",
                    height: "600px",
                    borderRadius: "8px",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                />
              )}
              <Box
                component="iframe"
                src={file}
                title={displayFileName}
                onLoad={handleLoad}
                onError={handleError}
                sx={{
                  width: "100%",
                  height: "600px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  visibility: isLoading ? "hidden" : "visible",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                p: 4,
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <FileText size={64} style={{ color: "#6b7280" }} />

              <Typography variant="body2" sx={{ color: "#6b7280", mt: 2 }}>
                File: {displayFileName}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={(theme) => ({
          [theme.breakpoints.down("sm")]: {
            flexWrap: "wrap",
            gap: 1,
            "& > :not(style) ~ :not(style)": { marginLeft: 0 },
            "& .MuiButton-root": {
              fontSize: "0.75rem",
              minWidth: 0,
              px: 1,
              flexShrink: 0,
            },
            "& .MuiButton-startIcon": { marginLeft: 0, marginRight: "4px" },
          },
        })}
      >
        <Button onClick={onClose}>Close</Button>

        <Button
          variant="contained"
          onClick={handleOpenURL}
          startIcon={<ExternalLink size={18} />}
        >
          Open
        </Button>

        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={downloading}
          startIcon={<Download size={18} />}
        >
          {downloading ? "Downloading..." : "Download"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default FileViewDialog;